import React, { useMemo } from 'react';

import { useDatasetDescription, useDatasetFields } from '../../../shared/api/apiHooks';
import { FieldFilters } from '../FieldFilters/FieldFilters';
import { DatasetFieldsListTable } from '../DatasetFieldsListTable/DatasetFieldsListTable';
import { useFilterPaginagion } from '../Filters/auxiliaryFields';

import { useWatch } from 'react-hook-form';
import { FiltersControl } from '../Filters/types';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { DatasetRegionsTable } from '../RegionTables/DatasetRegionsTable';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import Skeleton from 'react-loading-skeleton';
import { ListResources } from './ListResources';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';
import { Theme } from '../../../shared/api/api';
import { AlphaStatusThemeLabel } from '../../../components/HOCStageChooser/HOCStageChooser';
import { useIntl } from 'react-intl';
import { CategoryLinks } from '../DataField/DataField';
import { useSelector } from 'react-redux';
import { hasConsultantOrResearcherPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

interface DescriptionTabProps {
  datasetId: string;
  control: FiltersControl;
}

export function DescriptionTab({ datasetId, control }: DescriptionTabProps) {
  const datasetDescriptionResult = useDatasetDescription({ id: datasetId });
  const intl = useIntl();

  const { region, delay, universe } = control.getValues();
  const currentUniverseThemes = useMemo(
    () =>
      datasetDescriptionResult.data?.data?.find(
        field => field.delay.toString() === delay && field.region === region && field.universe === universe
      )?.themes ?? [],
    [delay, region, universe, datasetDescriptionResult]
  );
  const { isLoading: isDatasetDescriptionLoading, data: datasetDescription } = datasetDescriptionResult;

  if (!datasetId) {
    return <div>No dataset id — no dataset</div>;
  }

  return (
    <>
      <h2>
        <FormattedMessageHelper id="dataExplorer.dataset.description" />
      </h2>
      <div className="data-single-entity__description">
        {isDatasetDescriptionLoading ? (
          <>
            <Skeleton count={1} width={300} />
            <Skeleton count={1} width={200} />
            <br />
            <Skeleton count={4} />
          </>
        ) : (
          <>
            <CategoryLinks category={datasetDescription?.category} subcategory={datasetDescription?.subcategory} />
            <p className="data-field__type">
              <span>
                <FormattedMessageHelper id="dataExplorer.dataset.id" />:<span className="data-field__type-value"> {datasetId}</span>
              </span>
              <CopyToClipboard className="copy-to-clipboard--field-page copy-to-clipboard--dataset-description" value={datasetId} />
            </p>
            <br />
            <p>{datasetDescription?.description}</p>
          </>
        )}
      </div>

      <div className="data-single-entity__themes">
        {currentUniverseThemes &&
          currentUniverseThemes?.length > 0 &&
          (currentUniverseThemes as unknown as Theme[])?.map(theme => <AlphaStatusThemeLabel key={theme.id} theme={theme} intl={intl} />)}
      </div>

      <DatasetRegionsTable datasetDescriptionResult={datasetDescriptionResult} control={control} />

      {datasetDescription && datasetDescription.researchPapers.length > 0 && (
        <ListResources resources={datasetDescription.researchPapers} />
      )}
    </>
  );
}

interface FieldsTabProps {
  datasetId: string;
  defaultValues: any;
  control: FiltersControl;
  queryObject: any;
  reset: () => void;
}

export function FieldsTab({ datasetId, defaultValues, control, queryObject, reset }: FieldsTabProps) {
  const fieldsResult = useDatasetFields({ queryObject });
  const isPyramidMultiplierVisible = useSelector(hasConsultantOrResearcherPermission);
  const filterPagination = useFilterPaginagion({ control, defaultValues });
  const isSearchActive = (useWatch({ name: filterFields.search.name, control }) || '').trim() !== '';

  return (
    <>
      <FieldFilters isExtraFilterChanged={false} control={control} reset={reset} />
      <DatasetFieldsListTable
        isSearchActive={isSearchActive}
        filterPagination={filterPagination}
        control={control}
        result={fieldsResult}
        datasetId={datasetId}
        isPyramidMultiplierVisible={isPyramidMultiplierVisible}
      />
    </>
  );
}
